import type {
  AddFavoriteSuccessResponse,
  RemoveFavoriteSuccessResponse,
  CreateTempPatternResponse,
  PatternTempResponse,
  UpdateTempPatternRequest,
  DeleteTempPatternResponse,
  CreateDraftPatternResponse,
  FetchPatternDraftResponse,
  UpdateDraftPatternResponse,
  PublishPatternResponse,
  RepublishPatternResponse,
  MigrateTempImagesResponse,
  TempToDraftMigrationRequest,
  TempToDraftMigrationResponse,
  PatternModerationHistoryResponse,
  PatternNotesResponse,
  CreateNoteResponse,
  GetUserAppealsResponse,
  ArchivePatternResponse,
  UnarchivePatternResponse,
  CreateAppealResponse,
  CheckSubmissionLimitResponse,
  SuccessResponse,
  ValidateMaterialArchiveResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

// Favorite/Unfavorite
export async function addFavoritePattern(patternId: string, idToken: string): Promise<AddFavoriteSuccessResponse> {
  return secureFetch<AddFavoriteSuccessResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`, idToken, {
    method: 'POST',
  })
}

export async function removeFavoritePattern(
  patternId: string,
  idToken: string,
): Promise<RemoveFavoriteSuccessResponse> {
  return secureFetch<RemoveFavoriteSuccessResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/favorite`, idToken, {
    method: 'DELETE',
  })
}

// Temp Pattern
export async function createTempPattern(idToken: string): Promise<CreateTempPatternResponse> {
  return secureFetch<CreateTempPatternResponse>(`${SECURE_API_BASE}/pattern/temp`, idToken, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function fetchTempPattern(patternId: string, idToken: string): Promise<PatternTempResponse> {
  return secureFetch<PatternTempResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'GET',
  })
}

export async function updateTempPattern(
  patternId: string,
  tempData: UpdateTempPatternRequest,
  idToken: string,
): Promise<PatternTempResponse> {
  return secureFetch<PatternTempResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'PUT',
    body: JSON.stringify(tempData),
  })
}

export async function deleteTempPattern(patternId: string, idToken: string): Promise<DeleteTempPatternResponse> {
  return secureFetch<DeleteTempPatternResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/temp`, idToken, {
    method: 'DELETE',
  })
}

// uploadTempPatternImage available via secure/images.ts

// Draft Pattern
export async function createDraftPattern(
  idToken: string,
  data: Record<string, unknown> = {},
): Promise<CreateDraftPatternResponse> {
  return secureFetch<CreateDraftPatternResponse>(`${SECURE_API_BASE}/pattern/draft`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function fetchPatternDraft(patternId: string, idToken: string): Promise<FetchPatternDraftResponse> {
  return secureFetch<FetchPatternDraftResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`, idToken, {
    method: 'GET',
  })
}

export async function updateDraftPattern(
  patternId: string,
  draftData: unknown,
  idToken: string,
): Promise<UpdateDraftPatternResponse> {
  return secureFetch<UpdateDraftPatternResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(draftData),
  })
}

// Publish/Unpublish
export async function publishPattern(patternId: string, idToken: string): Promise<PublishPatternResponse> {
  return secureFetch<PublishPatternResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/publish`, idToken, {
    method: 'POST',
  })
}

export async function republishPattern(patternId: string, idToken: string, changeNotes: string): Promise<RepublishPatternResponse> {
  return secureFetch<RepublishPatternResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/republish`, idToken, {
    method: 'POST',
    body: JSON.stringify({ change_notes: changeNotes }),
  })
}

export async function unpublishPattern(patternId: string, idToken: string): Promise<void> {
  return secureFetch<void>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(patternId)}/unpublish`, idToken, {
    method: 'POST',
  })
}

// uploadPatternImage available via secure/images.ts

// Image Migration
export async function migrateTempImages(
  draftPatternId: string,
  tempPatternId: string,
  idToken: string,
): Promise<MigrateTempImagesResponse> {
  return secureFetch<MigrateTempImagesResponse>(`${SECURE_API_BASE}/pattern/${encodeURIComponent(draftPatternId)}/migrate-images`, idToken, {
    method: 'POST',
    body: JSON.stringify({ tempPatternId }),
  })
}

export async function migrateTempToDraft(
  tempPatternId: string,
  idToken: string,
): Promise<TempToDraftMigrationResponse> {
  try {
    const requestData: TempToDraftMigrationRequest = { tempPatternId }
    const result = await secureFetch<TempToDraftMigrationResponse>(`${SECURE_API_BASE}/pattern/temp-to-draft`, idToken, {
      method: 'POST',
      body: JSON.stringify(requestData),
    })

    localStorage.removeItem('tempPatternId')

    return result
  } catch (error: unknown) {
    console.error('Failed to migrate temp to draft:', error)
    throw new Error('Failed to save pattern as draft')
  }
}

// Archive
export async function archivePattern(
  patternId: string,
  reason: string | undefined,
  idToken: string,
): Promise<ArchivePatternResponse> {
  return secureFetch<ArchivePatternResponse>(`${SECURE_API_BASE}/patterns/${encodeURIComponent(patternId)}/archive`, idToken, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export async function unarchivePattern(
  patternId: string,
  idToken: string,
): Promise<UnarchivePatternResponse> {
  return secureFetch<UnarchivePatternResponse>(`${SECURE_API_BASE}/patterns/${encodeURIComponent(patternId)}/unarchive`, idToken, {
    method: 'POST',
  })
}

// Moderation History & Notes
export async function fetchPatternModerationHistory(
  patternId: string,
  targetVersion: number,
  idToken: string,
): Promise<PatternModerationHistoryResponse> {
  return secureFetch<PatternModerationHistoryResponse>(
    `${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/version/${targetVersion}/history`,
    idToken,
    { method: 'GET' },
  )
}

export async function fetchPatternNotes(patternId: string, idToken: string): Promise<PatternNotesResponse> {
  return secureFetch<PatternNotesResponse>(`${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`, idToken, {
    method: 'GET',
  })
}

export async function createPatternNote(
  patternId: string,
  content: string,
  category: string = 'other',
  severity: string = 'medium',
  idToken: string,
): Promise<CreateNoteResponse> {
  return secureFetch<CreateNoteResponse>(`${SECURE_API_BASE}/moderation/pattern/${encodeURIComponent(patternId)}/notes`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content, category, severity }),
  })
}

// Appeals & Submission Limits
export async function createAppeal(
  contentType: string,
  contentId: string,
  message: string,
  idToken: string,
): Promise<CreateAppealResponse> {
  return secureFetch<CreateAppealResponse>(`${SECURE_API_BASE}/appeal/${contentType}/${contentId}`, idToken, {
    method: 'POST',
    body: JSON.stringify({ message }),
  })
}

export async function getUserAppeals(idToken: string): Promise<GetUserAppealsResponse> {
  return secureFetch<GetUserAppealsResponse>(`${SECURE_API_BASE}/appeals`, idToken, {
    method: 'GET',
  })
}

export async function checkSubmissionLimit(
  contentType: string,
  contentId: string,
  idToken: string,
): Promise<CheckSubmissionLimitResponse> {
  return secureFetch<CheckSubmissionLimitResponse>(`${SECURE_API_BASE}/submission-limit/${contentType}/${contentId}`, idToken, {
    method: 'GET',
  })
}

export async function recordSubmission(
  contentType: string,
  contentId: string,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch<SuccessResponse>(`${SECURE_API_BASE}/submission`, idToken, {
    method: 'POST',
    body: JSON.stringify({ contentType, contentId }),
  })
}

// Material Archive Validation (pattern-related)
export async function validateMaterialArchive(
  materialId: string,
  idToken: string,
): Promise<ValidateMaterialArchiveResponse> {
  return secureFetch<ValidateMaterialArchiveResponse>(
    `${SECURE_API_BASE}/craft-room/material/${encodeURIComponent(materialId)}/archive-validation`,
    idToken,
    {
      method: 'GET',
    },
  )
}
